import { Injectable } from '@angular/core';
import { PDFDocument } from '../interfaces/pdf-document.interface';
import { R8Entity, R8OwnershipLevel, R8ReportModel, R8Ubo } from '../models/r8-report.model';

const mm = (value: number): number => value * 72 / 25.4;
const PAGE_WIDTH = 595.28;
const PAGE_HEIGHT = 841.89;
const LEFT = mm(14.2);
const RIGHT = PAGE_WIDTH - LEFT;
const CONTENT_TOP = mm(36.7);
const CONTENT_BOTTOM = PAGE_HEIGHT - mm(18.2);
const FOOTER_TOP = PAGE_HEIGHT - mm(7.4);
const COLORS = {
  navy: '#0b2130', dark: '#071f2d', title: '#081d2b', cyan: '#11aed7',
  border: '#d7e1e6', rule: '#dce4e8', label: '#677b87', value: '#4c606c',
  light: '#f6f9fa', muted: '#778893', white: '#ffffff'
};

interface Field { label: string; value?: string; }

@Injectable({ providedIn: 'root' })
export class R8PdfRendererService {
  private y = 0;
  private pageNumber = 1;

  drawDocument(doc: PDFDocument, model: R8ReportModel, coverImage: string): void {
    this.drawCover(doc, model, coverImage);
    doc.addPage({ size: 'A4', margins: { top: 0, right: 0, bottom: 0, left: 0 } });
    this.pageNumber = 2;
    this.startReportPage(doc, model, false);
    this.drawCoreParties(doc, model);

    if (model.ownershipLevels.length) this.drawOwnership(doc, model, model.ownershipLevels);
    if (model.ubos.length) this.drawUbos(doc, model, model.ubos);
    if (model.references.length) this.drawReferences(doc, model, model.references);

    this.drawFooters(doc);
  }

  private drawCover(doc: PDFDocument, model: R8ReportModel, coverImage: string): void {
    doc.image(coverImage, 0, 0, { width: PAGE_WIDTH, height: PAGE_HEIGHT });
    const x = mm(15.4);
    const width = mm(145);
    let y = mm(59);
    doc.font('Helvetica-Bold').fontSize(43).fillColor('#f4f7f9')
      .text('Vessel UCB', x, y, { width, characterSpacing: -0.7 });
    y += 43 * 1.03 + mm(7.5);
    doc.font('Helvetica-Bold').fontSize(18).fillColor('#b8d4ee')
      .text(`IMO ${model.vesselImo}`, x, y, { width, characterSpacing: 0.1 });
    y += 18 * 1.12 + mm(7.2);
    doc.rect(x, y, width * 0.83, mm(0.42)).fill('#39bde5');
    doc.rect(x + width * 0.83, y, width * 0.15, mm(0.42)).fill('#95eaff');
    y += mm(0.42) + mm(5.8);
    doc.font('Helvetica-Oblique').fontSize(14).fillColor('#bec5cc')
      .text('Upward Control & Beneficial Ownership', x, y, { width });
  }

  private startReportPage(doc: PDFDocument, model: R8ReportModel, continued: boolean): void {
    if (this.pageNumber > 2) {
      doc.addPage({ size: 'A4', margins: { top: 0, right: 0, bottom: 0, left: 0 } });
    }
    this.drawPageHeader(doc, model);
    let y = CONTENT_TOP;
    doc.font('Helvetica-Bold').fontSize(8).fillColor(COLORS.cyan)
      .text('STRUCTURED OWNERSHIP DELIVERY', LEFT, y, { characterSpacing: 1.2 });
    y += 18;
    doc.font('Helvetica-Bold').fontSize(24.5).fillColor(COLORS.title)
      .text(continued ? 'Vessel UCB - Continued' : 'Vessel UCB', LEFT, y, { width: RIGHT - LEFT });
    y += 24.5 * 1.05 + mm(4.4);
    this.y = y;
  }

  private drawCoreParties(doc: PDFDocument, model: R8ReportModel): void {
    let blockHeight = this.sectionHeadingHeight() + mm(4);
    const imoHeight = this.imoHeight();
    blockHeight += imoHeight + mm(4);
    const parties = [model.shipOwner, model.shipManager].filter((value): value is R8Entity => !!value);
    const gap = mm(3.5);
    const cardWidth = parties.length === 1 ? RIGHT - LEFT : (RIGHT - LEFT - gap) / 2;
    const cardHeights = parties.map(party => this.entityCardHeight(doc, this.entityFields(party, party === model.shipOwner), cardWidth));
    blockHeight += Math.max(0, ...cardHeights);
    this.ensureBlock(doc, model, blockHeight);
    this.sectionHeading(doc, 'Vessel IMO & Core Parties', 'Schema-aligned record');

    this.drawImoBox(doc, model.vesselImo);
    this.y += imoHeight + mm(4);
    parties.forEach((party, index) => {
      const x = LEFT + (parties.length === 2 ? index * (cardWidth + gap) : 0);
      const isOwner = party === model.shipOwner;
      this.drawEntityCard(doc, x, this.y, cardWidth, isOwner ? 'Ship Owner' : 'Ship Manager', this.entityFields(party, isOwner));
    });
    this.y += Math.max(0, ...cardHeights) + mm(4);
  }

  private drawOwnership(doc: PDFDocument, model: R8ReportModel, levels: R8OwnershipLevel[]): void {
    const cardGap = mm(6);
    const cardWidth = (RIGHT - LEFT - cardGap * (levels.length - 1)) / levels.length;
    const heights = levels.map(level => this.ownershipCardHeight(doc, level, this.ownershipFields(level)));
    const sectionHeight = this.sectionHeadingHeight() + mm(2.7) + Math.max(...heights) + mm(4);
    this.ensureBlock(doc, model, sectionHeight);
    this.sectionHeading(doc, 'Ownership Levels', 'Level 1 → Level 3');
    levels.forEach((level, index) => this.drawOwnershipCard(
      doc, LEFT + index * (cardWidth + cardGap), this.y, cardWidth, index + 1, level, this.ownershipFields(level)
    ));
    this.y += Math.max(...heights) + mm(4);
  }

  private drawUbos(doc: PDFDocument, model: R8ReportModel, ubos: R8Ubo[]): void {
    for (let index = 0; index < ubos.length; index++) {
      const ubo = ubos[index];
      const reasonParts = this.chunkText(ubo.reasoning || '', 850);
      for (let part = 0; part < reasonParts.length; part++) {
        const continuation = part > 0;
        const fields = continuation
          ? [{ label: 'Reasoning', value: reasonParts[part] }]
          : this.uboFields(ubo, reasonParts[part]);
        const height = this.uboCardHeight(doc, fields);
        const needsSectionHeading = index === 0 && part === 0;
        const headingHeight = needsSectionHeading ? this.sectionHeadingHeight() + mm(2.7) : 0;
        if (this.y + headingHeight + height > CONTENT_BOTTOM) {
          this.startContinuation(doc, model);
          this.sectionHeading(doc, 'Ultimate Beneficial Owners', `UBO ${index + 1} to UBO ${ubos.length}`);
        } else if (needsSectionHeading) {
          this.sectionHeading(doc, 'Ultimate Beneficial Owners', 'UBO 1 to UBO 9');
        }
        this.drawUboCard(doc, this.y, RIGHT - LEFT, index + 1, ubo, fields, continuation);
        this.y += height + mm(4);
      }
    }
  }

  private drawReferences(doc: PDFDocument, model: R8ReportModel, references: string[]): void {
    const headingHeight = this.sectionHeadingHeight() + mm(2.7);
    if (this.y + headingHeight + mm(12) > CONTENT_BOTTOM) this.startContinuation(doc, model);
    this.sectionHeading(doc, 'References', 'Source URLs');
    references.forEach((reference, index) => {
      const text = this.wrapText(doc, `${reference}`, 6.05, RIGHT - LEFT - mm(10));
      const lineHeight = 6.05 * 1.22;
      const rowHeight = text.lines.length * lineHeight + mm(1.45);
      const dividerHeight = mm(0.2);
      const rowGap = mm(1.45);
      if (this.y + rowHeight + dividerHeight + rowGap > CONTENT_BOTTOM) {
        this.startContinuation(doc, model);
      }
      doc.font('Helvetica-Bold').fontSize(6.6).fillColor(COLORS.cyan)
        .text(String(index + 1).padStart(2, '0'), LEFT, this.y + 1, { width: mm(8) });
      doc.font('Helvetica').fontSize(6.05).fillColor('#536b78')
        .text(text.lines.join('\n'), LEFT + mm(10), this.y, { width: RIGHT - LEFT - mm(10) });
      this.y += rowHeight;
      doc.rect(LEFT, this.y, RIGHT - LEFT, dividerHeight).fill('#e1e8eb');
      this.y += dividerHeight + rowGap;
    });
  }

  private sectionHeading(doc: PDFDocument, title: string, meta: string): number {
    const y = this.y;
    doc.font('Helvetica-Bold').fontSize(14.5).fillColor('#0a2130')
      .text(title, LEFT, y, { width: mm(140) });
    doc.font('Helvetica-Bold').fontSize(6.1).fillColor('#6b7d88')
      .text(meta.toUpperCase(), LEFT, y + 7, { width: RIGHT - LEFT, align: 'right', characterSpacing: 0.6 });
    const ruleY = y + 14.5 + mm(1.8);
    doc.rect(LEFT, ruleY, RIGHT - LEFT, mm(0.43)).fill(COLORS.navy);
    this.y = ruleY + mm(2.7);
    return this.sectionHeadingHeight();
  }

  private sectionHeadingHeight(): number { return 14.5 + mm(1.8) + mm(0.43) + mm(2.7); }

  private drawImoBox(doc: PDFDocument, imo: string): void {
    const x = LEFT;
    const y = this.y;
    const width = mm(52);
    const height = this.imoHeight();
    doc.save();
    doc.roundedRect(x, y, width, height, mm(2)).clip();
    doc.rect(x, y, width, height).fill(COLORS.white);
    doc.rect(x, y, width, mm(0.9)).fill('#16b4da');
    doc.font('Helvetica-Bold').fontSize(6.5).fillColor('#6b7d88')
      .text('VESSEL IMO', x + mm(4), y + mm(3.7), { characterSpacing: 0.8 });
    doc.font('Helvetica-Bold').fontSize(19).fillColor('#0a2130')
      .text(imo, x + mm(4), y + mm(7.6));
    doc.restore();
    this.drawRoundedOutline(doc, x, y, width, height, COLORS.border, mm(0.3), mm(2));
  }

  private imoHeight(): number { return mm(3.7 + 2.8 + 1.8 + 6.7 + 3.7); }

  private drawEntityCard(doc: PDFDocument, x: number, y: number, width: number, title: string, fields: Field[]): void {
    const height = this.entityCardHeight(doc, fields, width);
    doc.save();
    doc.roundedRect(x, y, width, height, mm(1.8)).clip();
    doc.rect(x, y, width, height).fill(COLORS.white);
    const titleHeight = mm(2.35) + 8.2 * 1.1 + mm(2.35);
    doc.rect(x, y, width, titleHeight).fill(COLORS.dark);
    doc.font('Helvetica-Bold').fontSize(8.2).fillColor(COLORS.white)
      .text(title, x + mm(2.7), y + mm(2.35), { width: width - mm(5.4) });
    let rowY = y + titleHeight;
    const labelWidth = width * 0.39;
    fields.forEach(field => {
      const wrapped = this.wrapText(doc, field.value || '', 6.8, width - labelWidth - mm(4.1));
      const labelLines = this.wrapText(doc, field.label.toUpperCase(), 5.8, labelWidth - mm(4)).lines;
      const rowHeight = Math.max(mm(3.4), labelLines.length * 5.8 * 1.2 + mm(3.4), wrapped.lines.length * 6.8 * 1.3 + mm(3.4));
      doc.rect(x, rowY, labelWidth, rowHeight).fill(COLORS.light);
      doc.rect(x, rowY, width, mm(0.2)).fill(COLORS.rule);
      doc.font('Helvetica-Bold').fontSize(5.8).fillColor(COLORS.label)
        .text(labelLines.join('\n'), x + mm(2), rowY + mm(1.7), { width: labelWidth - mm(4), characterSpacing: 0.25 });
      doc.font('Helvetica').fontSize(6.8).fillColor(COLORS.value)
        .text(wrapped.lines.join('\n') || ' ', x + labelWidth + mm(2), rowY + mm(1.7), { width: width - labelWidth - mm(4) });
      rowY += rowHeight;
    });
    doc.restore();
    this.drawRoundedOutline(doc, x, y, width, height, COLORS.border, mm(0.26), mm(1.8));
  }

  private drawOwnershipCard(doc: PDFDocument, x: number, y: number, width: number, index: number, level: R8OwnershipLevel, fields: Field[]): void {
    const height = this.ownershipCardHeight(doc, level, fields);
    doc.save();
    doc.roundedRect(x, y, width, height, mm(1.8)).clip();
    doc.rect(x, y, width, height).fill(COLORS.white);
    doc.rect(x, y, width, mm(0.85)).fill('#16b4da');
    const nameLines = this.wrapText(doc, level.name || '', 8.2, width - mm(5)).lines;
    const titleHeight = mm(2.5) + 5.8 * 1.2 + mm(0.9) + 8.2 * 1.25 * Math.max(nameLines.length, 1) + mm(2.1);
    doc.rect(x, y + mm(0.85), width, titleHeight).fill('#f7fafb');
    doc.font('Helvetica-Bold').fontSize(5.8).fillColor('#6b7d88')
      .text(`OWNERSHIP LEVEL ${index}`, x + mm(2.5), y + mm(3.35), { characterSpacing: 0.5 });
    doc.font('Helvetica-Bold').fontSize(8.2).fillColor('#0a2130')
      .text(nameLines.join('\n'), x + mm(2.5), y + mm(2.5) + 5.8 * 1.2 + mm(0.9), { width: width - mm(5) });
    let rowY = y + mm(0.85) + titleHeight;
    fields.forEach(field => {
      const wrapped = this.wrapText(doc, field.value || '', 6.15, width - mm(4));
      const labelHeight = Math.max(mm(2), 5.15 * 1.2);
      const valueHeight = Math.max(mm(2.5), wrapped.lines.length * 6.15 * 1.25);
      doc.rect(x, rowY, width, mm(0.2)).fill('#e1e8eb');
      doc.font('Helvetica-Bold').fontSize(5.15).fillColor(COLORS.label)
        .text(field.label.toUpperCase(), x + mm(2), rowY + mm(1.25), { characterSpacing: 0.2 });
      doc.font('Helvetica').fontSize(6.15).fillColor(COLORS.value)
        .text(wrapped.lines.join('\n'), x + mm(2), rowY + mm(1.25) + labelHeight, { width: width - mm(4) });
      rowY += mm(1.25) + labelHeight + valueHeight;
    });
    doc.restore();
    this.drawRoundedOutline(doc, x, y, width, height, COLORS.border, mm(0.26), mm(1.8));
  }

  private drawUboCard(doc: PDFDocument, y: number, width: number, index: number, ubo: R8Ubo, fields: Field[], continuation: boolean): void {
    const cardHeight = this.uboCardHeight(doc, fields);
    const x = LEFT;
    doc.save();
    doc.roundedRect(x, y, width, cardHeight, mm(1.8)).clip();
    doc.rect(x, y, width, cardHeight).fill(COLORS.white);
    doc.rect(x, y, mm(1), cardHeight).fill(COLORS.cyan);
    const titleHeight = mm(2.5) + 6 + mm(0.9) + 10.2 * 1.18 + mm(2.5);
    doc.rect(x + mm(1), y, width - mm(1), titleHeight).fill(COLORS.dark);
    doc.font('Helvetica-Bold').fontSize(6).fillColor('#68d9f4')
      .text(`ULTIMATE BENEFICIAL OWNER ${index}`, x + mm(3), y + mm(2.5), { characterSpacing: 0.6 });
    doc.font('Helvetica-Bold').fontSize(10.2).fillColor(COLORS.white)
      .text(continuation ? `${ubo.name || `UBO ${index}`} - Reasoning Continued` : (ubo.name || `UBO ${index}`), x + mm(3), y + mm(5.2), { width: width - mm(35) });
    const meta = continuation ? 'CONTINUATION' : [ubo.entityType, ubo.country].filter(Boolean).join(' - ');
    if (meta) {
      doc.font('Helvetica').fontSize(6.3).fillColor('#cbd8df')
        .text(meta, x + width - mm(50), y + mm(6), { width: mm(46), align: 'right' });
    }
    let rowY = y + titleHeight;
    fields.forEach(field => {
      const wrapped = this.wrapText(doc, field.value || '', 6.65, width * 0.75 - mm(4));
      const rowHeight = Math.max(mm(3.1), wrapped.lines.length * 6.65 * 1.2 + mm(3.1));
      doc.rect(x + mm(1), rowY, width - mm(1), mm(0.2)).fill(COLORS.rule);
      doc.rect(x + mm(1), rowY, width * 0.25, rowHeight).fill(COLORS.light);
      doc.font('Helvetica-Bold').fontSize(5.8).fillColor(COLORS.label)
        .text(field.label.toUpperCase(), x + mm(3), rowY + mm(1.55), { width: width * 0.25 - mm(4) });
      doc.font('Helvetica').fontSize(6.65).fillColor(COLORS.value)
        .text(wrapped.lines.join('\n'), x + width * 0.25 + mm(2), rowY + mm(1.55), { width: width * 0.75 - mm(4) });
      rowY += rowHeight;
    });
    doc.restore();
    this.drawRoundedOutline(doc, x, y, width, cardHeight, COLORS.border, mm(0.26), mm(1.8));
  }

  private drawPageHeader(doc: PDFDocument, model: R8ReportModel): void {
    // Kept as a separate method so continuation pages use the same fixed header.
    doc.font('Times-Roman').fontSize(17).fillColor(COLORS.navy)
      .text('TEVUNA', LEFT, mm(15.3), { characterSpacing: 4.8 });
    const headerWidth = mm(72);
    doc.font('Helvetica-Bold').fontSize(9).fillColor(COLORS.navy)
      .text('VESSEL UCB', RIGHT - headerWidth, mm(14.6), { width: headerWidth, align: 'right', characterSpacing: 0.5 });
    doc.font('Helvetica').fontSize(7.8).fillColor('#697b88')
      .text(`IMO ${model.vesselImo}`, RIGHT - headerWidth, mm(19.3), { width: headerWidth, align: 'right', characterSpacing: 0.8 });
    doc.rect(LEFT, mm(28.1), RIGHT - LEFT, mm(0.38)).fill(COLORS.navy);
  }

  private entityFields(entity: R8Entity, isOwner: boolean): Field[] {
    const prefix = isOwner ? 'Ship Owner' : 'Ship Manager';
    return [
      { label: `${prefix} Tevuna ID`, value: entity.tevunaId }, { label: `${prefix} Name`, value: entity.name },
      { label: `${prefix} Email`, value: entity.email }, { label: `${prefix} Phone`, value: entity.phone },
      { label: `${prefix} Registered Address`, value: entity.registeredAddress },
      { label: `${prefix} Country`, value: entity.country }, { label: `${prefix} City`, value: entity.city },
      ...(isOwner ? [{ label: `${prefix} Confidence Score`, value: entity.confidenceScore }] : [])
    ].filter(field => !!field.value);
  }

  private ownershipFields(level: R8OwnershipLevel): Field[] {
    return [
      { label: 'Tevuna ID', value: level.tevunaId }, { label: 'Name', value: level.name },
      { label: 'Address', value: level.address }, { label: 'City', value: level.city },
      { label: 'Country', value: level.country }, { label: 'Email', value: level.email },
      { label: 'Website', value: level.website }
    ].filter(field => !!field.value);
  }

  private uboFields(ubo: R8Ubo, reasoning = ubo.reasoning): Field[] {
    return [
      { label: 'Tevuna ID', value: ubo.tevunaId }, { label: 'Name', value: ubo.name },
      { label: 'Country', value: ubo.country }, { label: 'Start Date', value: ubo.startDate },
      { label: 'Percentage', value: ubo.percentage }, { label: 'Confidence', value: ubo.confidence },
      { label: 'Reasoning', value: reasoning }, { label: 'Entity Type', value: ubo.entityType }
    ].filter(field => !!field.value);
  }

  private entityCardHeight(doc: PDFDocument, fields: Field[], cardWidth: number): number {
    return mm(2.35 + 2.35) + 8.2 * 1.1 + fields.reduce((sum, field) => {
      const wrapped = this.wrapText(doc, field.value || '', 6.8, cardWidth * 0.61 - mm(4));
      const labelLines = this.wrapText(doc, field.label.toUpperCase(), 5.8, cardWidth * 0.39 - mm(4)).lines;
      return sum + Math.max(mm(3.4), labelLines.length * 5.8 * 1.2 + mm(3.4), wrapped.lines.length * 6.8 * 1.3 + mm(3.4));
    }, 0);
  }

  private ownershipCardHeight(doc: PDFDocument, level: R8OwnershipLevel, fields: Field[]): number {
    const nameLines = this.wrapText(doc, level.name || '', 8.2, mm(56) - mm(5)).lines.length;
    const titleHeight = mm(0.85 + 2.5 + 0.9 + 2.1) + 5.8 * 1.2 + 8.2 * 1.25 * Math.max(nameLines, 1);
    return titleHeight + fields.reduce((sum, field) => {
      const wrapped = this.wrapText(doc, field.value || '', 6.15, mm(48));
      return sum + mm(1.25) + Math.max(mm(2), 5.15 * 1.2) + Math.max(mm(2.5), wrapped.lines.length * 6.15 * 1.25);
    }, 0);
  }

  private uboCardHeight(doc: PDFDocument, fields: Field[]): number {
    return mm(2.5 + 2.5) + 6 + mm(0.9) + 10.2 * 1.18 + fields.reduce((sum, field) => {
      const wrapped = this.wrapText(doc, field.value || '', 6.65, mm(120));
      return sum + Math.max(mm(3.1), wrapped.lines.length * 6.65 * 1.2 + mm(3.1));
    }, 0);
  }

  private wrapText(doc: PDFDocument | undefined, text: string, fontSize: number, width: number): { lines: string[] } {
    if (!text) return { lines: [] };
    if (doc) {
      doc.font('Helvetica').fontSize(fontSize);
      const words = text.split(/\s+/);
      const lines: string[] = [];
      let line = '';
      words.forEach(word => {
        if (doc.widthOfString(word) > width) {
          if (line) { lines.push(line); line = ''; }
          let segment = '';
          for (const character of word) {
            if (segment && doc.widthOfString(segment + character) > width) {
              lines.push(segment);
              segment = character;
            } else segment += character;
          }
          line = segment;
          return;
        }
        const candidate = line ? `${line} ${word}` : word;
        if (line && doc.widthOfString(candidate) > width) { lines.push(line); line = word; }
        else line = candidate;
      });
      if (line) lines.push(line);
      return { lines };
    }
    const chars = Math.max(10, Math.floor(width / (fontSize * 0.48)));
    const words = text.split(/\s+/);
    const lines: string[] = [];
    let line = '';
    words.forEach(word => {
      if (word.length > chars) {
        if (line) { lines.push(line); line = ''; }
        for (let index = 0; index < word.length; index += chars) lines.push(word.slice(index, index + chars));
        return;
      }
      const candidate = line ? `${line} ${word}` : word;
      if (line && candidate.length > chars) { lines.push(line); line = word; }
      else line = candidate;
    });
    if (line) lines.push(line);
    return { lines };
  }

  private chunkText(value: string, maxChars: number): string[] {
    if (!value || value.length <= maxChars) return [value];
    const words = value.split(/\s+/);
    const chunks: string[] = [];
    let current = '';
    words.forEach(word => {
      const candidate = current ? `${current} ${word}` : word;
      if (current && candidate.length > maxChars) { chunks.push(current); current = word; }
      else current = candidate;
    });
    if (current) chunks.push(current);
    return chunks;
  }

  private ensureBlock(doc: PDFDocument, model: R8ReportModel, height: number): void {
    if (this.y + height <= CONTENT_BOTTOM) return;
    this.startContinuation(doc, model);
  }

  private startContinuation(doc: PDFDocument, model: R8ReportModel): void {
    this.pageNumber++;
    this.startReportPage(doc, model, true);
  }

  private drawFooters(doc: PDFDocument): void {
    const pages = doc.bufferedPageRange();
    for (let index = 0; index < pages.count; index++) {
      doc.switchToPage((pages.start ?? 0) + index);
      if (index === 0) continue;
      const oldBottom = doc.page.margins.bottom;
      doc.page.margins.bottom = 0;
      const y = FOOTER_TOP;
      doc.rect(LEFT, y - mm(2.5), RIGHT - LEFT, mm(0.23)).fill(COLORS.rule);
      doc.font('Helvetica').fontSize(6.4).fillColor(COLORS.muted)
        .text('Tevuna Insights LLC  |  Confidential  |  Vessel UCB', LEFT, y, { width: mm(130) });
      doc.font('Helvetica-Bold').fontSize(7).fillColor(COLORS.navy)
        .text(String(index + 1), RIGHT - mm(8), y, { width: mm(8), align: 'right' });
      doc.page.margins.bottom = oldBottom;
    }
  }

  private drawRoundedOutline(doc: PDFDocument, x: number, y: number, width: number, height: number, color: string, thickness: number, radius: number): void {
    doc.roundedRect(x, y, width, height, radius).strokeColor(color).lineWidth(thickness).stroke();
  }
}
